import { useEffect, useState } from "react";
import { ShieldCheck, FileDown, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FONTE = { studio: "registrato dallo studio", area_cliente: "accettato dal cliente nell'area clienti", firma_remota: "firmato a distanza con il mandato" };
const fmt = (s) => { try { return new Date(s).toLocaleString("it-IT", { timeZone: "Europe/Rome" }); } catch { return s; } };

export async function downloadGdprExport(aid, formato, tenantId = "") {
  const { data, headers } = await api.get(`/gdpr/clienti/${aid}/export`, { params: { formato, tenant_id: tenantId }, responseType: "blob" });
  const m = /filename\*=UTF-8''([^;]+)/.exec(headers["content-disposition"] || "");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(data); a.download = m ? decodeURIComponent(m[1]) : `dati-cliente.${formato}`;
  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

// Privacy del cliente: consenso al trattamento (data, ora, versione informativa) ed export dei dati.
export function GdprClientePanel({ anagId }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const owner = ["titolare", "amministratore"].includes(user?.role);
  useEffect(() => { api.get(`/anagrafiche/clienti/${anagId}/gdpr`).then(({ data }) => setD(data)).catch(() => setD(false)); }, [anagId]);
  if (d === false) return null;
  const act = async (revoca) => {
    if (revoca && !window.confirm("Registrare la revoca del consenso al trattamento dei dati?")) return;
    setBusy(revoca ? "revoca" : "consenso");
    try { const { data } = await api.post(`/anagrafiche/clienti/${anagId}/gdpr/consenso`, { revoca }); setD(data); toast.success(revoca ? "Revoca registrata" : "Consenso registrato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const exp = async (f) => { setBusy(f); try { await downloadGdprExport(anagId, f); } catch { toast.error("Esportazione non riuscita"); } finally { setBusy(""); } };
  const c = d?.consenso;
  const valido = c?.attivo !== false && c?.at;
  return (
    <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.05] p-3 space-y-2" data-testid="gdpr-cliente-panel">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-500" />
        <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Privacy — consenso trattamento dati</span>
        {d === null ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /> : (
          <span data-testid="gdpr-cliente-stato" className={`text-[11px] px-2 py-0.5 rounded-full border ${valido ? "border-emerald-500/50 text-emerald-600" : "border-amber-500/60 text-amber-600"}`}>
            {valido ? `Consenso del ${fmt(c.at)} · informativa v${c.versione}${c.versione < d.versione_corrente ? " (non aggiornata)" : ""}` : c?.revocato_at ? `Revocato il ${fmt(c.revocato_at)}` : "Consenso non registrato"}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={!!busy} onClick={() => act(false)} data-testid="gdpr-cliente-registra" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50">
          {busy === "consenso" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Registra consenso (v{d?.versione_corrente || "…"})
        </button>
        {valido && <button type="button" disabled={!!busy} onClick={() => act(true)} data-testid="gdpr-cliente-revoca" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-red-400/60 text-red-500 hover:bg-red-500/10 disabled:opacity-50"><Ban className="h-3.5 w-3.5" /> Revoca</button>}
        {owner && ["pdf", "csv"].map((f) => (
          <button key={f} type="button" disabled={!!busy} onClick={() => exp(f)} data-testid={`gdpr-cliente-export-${f}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-400/60 hover:bg-slate-500/10 disabled:opacity-50">
            {busy === f ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Esporta dati {f.toUpperCase()}
          </button>
        ))}
      </div>
      {d?.storico?.length > 0 && (
        <ul className="text-[11px] text-slate-500 space-y-0.5 max-h-28 overflow-y-auto" data-testid="gdpr-cliente-storico">
          {d.storico.map((s) => <li key={s.id}>{fmt(s.at)} — {s.revoca ? "Revoca" : "Consenso"} v{s.versione} — {FONTE[s.fonte] || s.fonte}{s.registrato_da ? ` (${s.registrato_da})` : ""}</li>)}
        </ul>
      )}
    </div>
  );
}
